import type { AudioEngine } from "../AudioEngine";
import type { BeatStep, ProceduralBeat } from "../ProceduralBeat";
import type { Composer } from "../composer/Composer";
import type { BarContext, BassRhythm } from "../composer/types";

// Chord-aware sub bass. Re-pitches on every bar to match the Composer's
// current chord and plays its root note on a configurable rhythm pattern
// instead of just on the kick. That alone turns it from a static root
// pedal into a real bassline.
//
// Always-on graph:
//   note osc -> envelope -> level gain -> musicDestination
// Each "note" allocates a fresh oscillator + envelope so we can have
// short legato overlap without click artefacts.

const DEFAULT_RHYTHM: BassRhythm = [0, 8];
const MIN_SUB_HZ = 35;
const MAX_SUB_HZ = 90;

export class SubBass {
  private engine: AudioEngine;
  private beat: ProceduralBeat;
  private composer: Composer | null = null;

  private level: GainNode | null = null;
  private rootHz = 55;
  private rhythm: BassRhythm = DEFAULT_RHYTHM;
  private targetGain = 0;
  private rampMs = 600;
  private playing = false;
  private unsubBeat: (() => void) | null = null;
  private unsubBar: (() => void) | null = null;
  private masterGainValue = 0;

  constructor(engine: AudioEngine, beat: ProceduralBeat) {
    this.engine = engine;
    this.beat = beat;
  }

  // Composer is optional; if absent, the sub falls back to playing the
  // last-known root on the default kick rhythm. This keeps the layer
  // useful for drum-only test setups.
  bindComposer(composer: Composer): void {
    if (this.composer === composer) return;
    if (this.unsubBar) this.unsubBar();
    this.composer = composer;
    this.unsubBar = composer.onBar.on((ctx) => this.onBar(ctx));
    // Adopt current chord immediately so a mid-bar bind doesn't lag.
    const cur = composer.getCurrentContext();
    if (cur) this.adoptChord(cur);
  }

  setRhythm(steps: BassRhythm): void {
    this.rhythm = steps.length > 0 ? steps.slice() : DEFAULT_RHYTHM;
  }

  start(): void {
    if (this.playing) return;
    const ctx = this.engine.context;
    const dest = this.engine.musicDestination ?? this.engine.destination;
    if (!ctx || !dest) return;
    this.level = ctx.createGain();
    this.level.gain.value = this.masterGainValue;
    this.level.connect(dest);
    this.unsubBeat = this.beat.onStep.on((s) => this.onBeat(s));
    this.playing = true;
  }

  stop(): void {
    if (!this.playing) return;
    this.unsubBeat?.();
    this.unsubBeat = null;
    if (this.unsubBar) {
      this.unsubBar();
      this.unsubBar = null;
    }
    if (this.level) {
      try { this.level.disconnect(); } catch { /* gone */ }
    }
    this.level = null;
    this.composer = null;
    this.playing = false;
  }

  setLevel(gain01: number, rampMs = 600): void {
    this.targetGain = Math.max(0, Math.min(1, gain01));
    this.rampMs = Math.max(20, rampMs);
    if (!this.level) {
      this.masterGainValue = this.targetGain;
      return;
    }
    const ctx = this.engine.context!;
    const t = ctx.currentTime;
    const g = this.level.gain;
    g.cancelScheduledValues(t);
    g.setValueAtTime(g.value, t);
    g.linearRampToValueAtTime(this.targetGain, t + this.rampMs / 1000);
    this.masterGainValue = this.targetGain;
  }

  // Manual pitch override. Composer-driven layers normally rely on
  // adoptChord() but the legacy MusicDirector.setKey path still calls
  // setPitch() so we keep it as a passthrough to the sub register.
  setPitch(rootHz: number): void {
    let hz = rootHz;
    while (hz > MAX_SUB_HZ) hz *= 0.5;
    while (hz < MIN_SUB_HZ) hz *= 2;
    this.rootHz = hz;
  }

  private onBar(ctx: BarContext): void {
    this.adoptChord(ctx);
  }

  private adoptChord(ctx: BarContext): void {
    let hz = ctx.chord.bassHz;
    while (hz > MAX_SUB_HZ) hz *= 0.5;
    while (hz < MIN_SUB_HZ) hz *= 2;
    this.rootHz = hz;
  }

  private onBeat(step: BeatStep): void {
    if (!this.level) return;
    if (this.targetGain <= 0.0001) return;
    if (!this.rhythm.includes(step.step)) return;
    this.scheduleNote(step.time);
  }

  private scheduleNote(time: number): void {
    const ctx = this.engine.context!;
    const osc = ctx.createOscillator();
    const env = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(this.rootHz, time);
    env.gain.setValueAtTime(0, time);
    env.gain.linearRampToValueAtTime(0.95, time + 0.01);
    env.gain.exponentialRampToValueAtTime(0.0001, time + 0.42);
    osc.connect(env).connect(this.level!);
    osc.start(time);
    osc.stop(time + 0.45);
  }
}
